import { useEffect, useState } from 'react';

/**
 * How tall the fixed navigation is, so the hero can fill exactly what is left of the
 * screen below it.
 *
 * The layout's UIProvider already measures this, but it lives inside NavigationMount
 * rather than around the whole tree, so a page is outside it and reads back a constant
 * zero. Measuring the header from here is what Pages/Reviews does for the same reason.
 *
 * Shared rather than owned by a page: the Magzter Gold and Zinio Unlimited heroes both
 * size themselves with it.
 *
 * Zero until the effect runs, exactly as headless behaved: its useResizeObserver also
 * reported nothing on the first render, so `calc(100vh - 0px)` is the height it painted
 * first there too.
 */
export default function useHeaderHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const header = document.querySelector('header');

    if (!header) {
      return;
    }

    const measure = () => setHeight(header.getBoundingClientRect().height);

    measure();

    // The header is two different heights across the mobile breakpoint and grows when
    // the search bar opens, so a single measurement on mount would be wrong by the
    // time anyone rotated a phone.
    const observer = new ResizeObserver(measure);
    observer.observe(header);

    return () => observer.disconnect();
  }, []);

  return height;
}
